import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  MenuItem,
  Button,
  Alert,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { getActivePractices, getReferringProviders, getSurgeons } from '../services/patientService';
import useDobConfirm from '../hooks/useDobConfirm';
import DobConfirmDialog from './DobConfirmDialog';

const INSURANCE_TYPES = [
  'PPO',
  'HMO',
  'MEDICAID',
  'POS',
  'Advantage (Non-HMO)',
  'Self-pay',
  'Workers Compensation',
  'Other'
];

const FILE_TYPES = [
  'PCP Referral',
  'H&P',
  'Progress Note',
  'Patient Demographics',
  'Radiology Report',
  'Lab Reports',
  'Patient ID',
  'Patients Insurance Card',
  'Other'
];

// Get the API base URL based on environment
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }
  return '';
};

export default function IncomingReferralForm() {
  // Practice selection
  const [practice, setPractice] = useState('');
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);

  // Preferred Surgeon
  const [preferredSurgeon, setPreferredSurgeon] = useState('ANY');
  const [surgeonsList, setSurgeonsList] = useState([]);
  const [surgeonsLoading, setSurgeonsLoading] = useState(false);

  // Referring Physician Info
  const [physicianName, setPhysicianName] = useState('');
  const [physicianPhone, setPhysicianPhone] = useState('');
  const [physicianFax, setPhysicianFax] = useState('');
  const [physicianOrganization, setPhysicianOrganization] = useState('');
  const [referringProvidersList, setReferringProvidersList] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [isExistingPhysician, setIsExistingPhysician] = useState(false);
  const [isPracticeLocked, setIsPracticeLocked] = useState(false);
  const [isPhysicianLocked, setIsPhysicianLocked] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [isOrganizationLocked, setIsOrganizationLocked] = useState(false);

  // Referral Coordinator Info
  const [coordinatorName, setCoordinatorName] = useState('');
  const [coordinatorEmail, setCoordinatorEmail] = useState('');

  // Patient Info
  const [patientName, setPatientName] = useState('');
  const [patientDOB, setPatientDOBRaw] = useState('');
  const { dobInputRef, showDobConfirmDialog, handleDobBlur, handleDobChange: setPatientDOB, confirmDob, fixDob } = useDobConfirm(patientDOB, setPatientDOBRaw);
  const [patientPhone, setPatientPhone] = useState('');
  const [patientPCP, setPatientPCP] = useState('');

  // Referral Details
  const [referralReason, setReferralReason] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [hmoAuthNumber, setHmoAuthNumber] = useState('');
  const [workerCompInfo, setWorkerCompInfo] = useState('');
  const [insuranceCarrier, setInsuranceCarrier] = useState('');
  const [memberId, setMemberId] = useState('');

  // File uploads
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Notes
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Form state
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load practices on mount & check URL parameters
  useEffect(() => {
    const loadPractices = async () => {
      try {
        const data = await getActivePractices();
        setPractices(data || []);
        
        // Parse URL parameters for pre-population
        const params = new URLSearchParams(window.location.search);
        const urlPractice = params.get('practice');
        const urlPhysician = params.get('physicianName');
        
        if (urlPractice) {
          // If practice code is passed (e.g. STARS)
          const matchedPractice = (data || []).find(
            p => p.practice_code === urlPractice || p.practice_name === urlPractice
          );
          if (matchedPractice) {
            setPractice(matchedPractice.practice_code);
            setIsPracticeLocked(true);
          }
        }
        if (urlPhysician) {
          setPhysicianName(urlPhysician);
          setIsPhysicianLocked(true);
        }
        const urlOrganization = params.get('organization');
        if (urlOrganization) {
          setSelectedOrganization(urlOrganization);
          setIsOrganizationLocked(true);
        }
      } catch (err) {
        console.error('Error loading practices:', err);
        setPractices([{ practice_name: 'STARS Plastic Surgery', practice_code: 'STARS' }]);
      } finally {
        setPracticesLoading(false);
      }
    };
    loadPractices();
  }, []);

  // Dynamically load referring providers when selected practice changes
  useEffect(() => {
    const loadProviders = async () => {
      if (!practice) {
        setReferringProvidersList([]);
        return;
      }
      setProvidersLoading(true);
      try {
        const res = await getReferringProviders(practice);
        if (res.success) {
          setReferringProvidersList(res.providers || []);
        }
      } catch (err) {
        console.error('Error loading EMR providers:', err);
        setReferringProvidersList([]);
      } finally {
        setProvidersLoading(false);
      }
    };
    loadProviders();
  }, [practice]);

  // Load surgeons for the selected practice
  useEffect(() => {
    const loadSurgeons = async () => {
      if (!practice) {
        setSurgeonsList([]);
        setPreferredSurgeon('ANY');
        return;
      }
      setSurgeonsLoading(true);
      try {
        const surgeons = await getSurgeons(practice);
        setSurgeonsList(surgeons || []);
      } catch (err) {
        console.error('Error loading surgeons:', err);
        setSurgeonsList([]);
      } finally {
        setSurgeonsLoading(false);
      }
    };
    loadSurgeons();
  }, [practice]);

  // Autodetect if typed/url physician matches EMR records
  useEffect(() => {
    if (referringProvidersList.length > 0 && physicianName && !isExistingPhysician) {
      const matched = referringProvidersList.find(
        p => p.name.toLowerCase() === physicianName.toLowerCase()
      );
      if (matched) {
        setPhysicianName(matched.name);
        setPhysicianPhone(matched.phone || '');
        setPhysicianFax(matched.fax || '');
        setPhysicianOrganization(matched.organization || '');
        if (matched.email) {
          setCoordinatorEmail(matched.email);
        }
        setIsExistingPhysician(true);
      }
    }
  }, [referringProvidersList, physicianName, isExistingPhysician]);

  const formatPhone = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (setter) => (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setter(value);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setUploadedFiles(prev => [...prev, ...files]);
    }
  };

  const handleFileUpload = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadedFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const renderFilePreview = (file) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');
    const isWord = file.name.endsWith('.doc') || file.name.endsWith('.docx');

    if (isImage) {
      try {
        const url = URL.createObjectURL(file);
        return (
          <Box
            component="img"
            src={url}
            alt={file.name}
            sx={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          />
        );
      } catch (e) {
        return <ImageIcon sx={{ fontSize: 28, color: 'text.secondary' }} />;
      }
    }

    if (isPdf) {
      return (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: 2,
            backgroundColor: 'rgba(239, 83, 80, 0.1)',
            color: '#ef5350',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(239, 83, 80, 0.2)'
          }}
        >
          <PictureAsPdfIcon sx={{ fontSize: 24 }} />
        </Box>
      );
    }

    if (isWord) {
      return (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: 2,
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            color: '#2196f3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(33, 150, 243, 0.2)'
          }}
        >
          <DescriptionIcon sx={{ fontSize: 24 }} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: 50,
          height: 50,
          borderRadius: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 24 }} />
      </Box>
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('practice_code', practice);
      formData.append('referred_to_practice', preferredSurgeon === 'ANY' ? '' : preferredSurgeon);
      formData.append('physician_name', physicianName);
      formData.append('physician_phone', formatPhone(physicianPhone));
      formData.append('physician_fax', formatPhone(physicianFax));
      formData.append('coordinator_name', coordinatorName);
      formData.append('coordinator_email', coordinatorEmail);
      formData.append('patient_name', patientName);
      formData.append('patient_dob', patientDOB);
      formData.append('patient_phone', formatPhone(patientPhone));
      formData.append('patient_pcp', patientPCP || '');
      formData.append('referral_reason', referralReason);
      formData.append('reason_for_visit', referralReason);
      formData.append('insurance_type', insuranceType);
      formData.append('hmo_auth_number', hmoAuthNumber || '');
      formData.append('worker_comp_info', workerCompInfo || 'N/A');
      formData.append('insurance_carrier', insuranceCarrier);
      formData.append('member_id', memberId);
      formData.append('additional_notes', additionalNotes || '');

      // Append uploaded files
      uploadedFiles.forEach((file) => {
        formData.append('files', file);
        formData.append('file_types', 'Referral Document');
      });

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/referrals`, {
        method: 'POST',
        body: formData // multipart/form-data
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit referral form.');
      }

      setSubmitSuccess(true);
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit referral. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => {
        // If a word is already all uppercase and has length 2-5 (acronyms like RFSA, MD, PCP)
        if (word === word.toUpperCase() && word.length >= 2 && word.length <= 5) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const matchesOrganization = (providerOrg, targetOrg) => {
    if (!providerOrg || !targetOrg) return false;
    
    const pOrg = providerOrg.toLowerCase().trim();
    const tOrg = targetOrg.toLowerCase().trim();
    
    // Substring match
    if (pOrg.includes(tOrg) || tOrg.includes(pOrg)) return true;
    
    // Acronym match
    const getAcronym = (str) => {
      return str
        .replace(/\band\b|\bor\b|\bthe\b|\bof\b/g, '')
        .split(/\s+/)
        .map(word => word.charAt(0))
        .join('');
    };
    
    const pAcronym = getAcronym(pOrg);
    const tAcronym = getAcronym(tOrg);
    
    if (pAcronym && tAcronym && (pAcronym.includes(tAcronym) || tAcronym.includes(pAcronym))) return true;
    
    return false;
  };

  const getFilteredProviders = () => {
    if (!selectedOrganization) return referringProvidersList;
    return referringProvidersList.filter(p => 
      matchesOrganization(p.organization, selectedOrganization)
    );
  };

  const isValid = Boolean(
    practice && physicianName && coordinatorEmail &&
    (isExistingPhysician || (physicianPhone && physicianPhone.length >= 10)) &&
    patientName && patientDOB && patientPhone &&
    referralReason && insuranceType && insuranceCarrier && memberId &&
    uploadedFiles.length > 0 &&
    (insuranceType !== 'Workers Compensation' || (workerCompInfo && workerCompInfo.trim() !== ''))
  );

  if (submitSuccess) {
    // Look up selected practice name for confirmation view
    const practiceObject = practices.find(p => p.practice_code === practice);
    const selectedPracticeName = practiceObject ? practiceObject.practice_name : practice;

    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
          <Alert severity="success" sx={{ mb: 3, justifyContent: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Referral Submitted Successfully!
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            Thank you for submitting the referral to <strong>{selectedPracticeName}</strong>.
          </Typography>
          <Typography variant="body1" paragraph>
            A confirmation email has been sent to <strong>{coordinatorEmail}</strong>.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 2 }}>
            The patient scheduling team will review the submitted medical records and contact your patient shortly.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button variant="contained" color="primary" href={window.location.pathname + window.location.search} sx={{ borderRadius: 2 }}>
              Return to Home
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 4 }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>
          Incoming Patient Referral
        </Typography>
        <Typography variant="body1" align="center" sx={{ mt: 1, opacity: 0.9 }}>
          Thank you for referring your patient to us!
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}



      {/* Practice Selection */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Select Practice
        </Typography>
        <TextField
          select
          fullWidth
          required
          label="Referring to Practice"
          value={practice}
          onChange={(e) => {
            setPractice(e.target.value);
            setPreferredSurgeon('ANY');
            setPhysicianName('');
            setPhysicianPhone('');
            setPhysicianFax('');
            setPhysicianOrganization('');
            setIsExistingPhysician(false);
          }}
          disabled={practicesLoading || isPracticeLocked}
        >
          {practices.map((p) => (
            <MenuItem key={p.practice_code} value={p.practice_code}>
              {p.practice_name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Preferred Surgeon */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Preferred Surgeon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Is there a specific surgeon you'd like your patient to be seen by?
        </Typography>
        <TextField
          select
          fullWidth
          label="Preferred Surgeon"
          value={preferredSurgeon}
          onChange={(e) => setPreferredSurgeon(e.target.value)}
          disabled={!practice || surgeonsLoading}
          helperText={!practice ? 'Select a practice first' : (surgeonsLoading ? 'Loading surgeons...' : 'Select "Any Surgeon" if you have no preference')}
        >
          <MenuItem value="ANY">Any Surgeon</MenuItem>
          {surgeonsList.map((s) => (
            <MenuItem key={s.id} value={s.name}>
              {s.name}{s.specialty ? ` — ${s.specialty}` : ''}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* Referring Physician Information */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Referring Physician Information
        </Typography>
        {selectedOrganization && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Filtered by organization:
            </Typography>
            <Chip 
              label={selectedOrganization} 
              onDelete={isOrganizationLocked ? undefined : () => setSelectedOrganization('')}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={getFilteredProviders()}
              filterOptions={(options, state) => {
                const input = (state.inputValue || '').trim().toLowerCase();
                if (input.length < 4) return [];
                return options.filter((o) => (o.name || '').toLowerCase().includes(input));
              }}
              loading={providersLoading}
              disabled={!practice || isPhysicianLocked}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || '';
              }}
              value={physicianName}
              onChange={(e, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  setPhysicianName(newValue.name);
                  setPhysicianPhone(newValue.phone || '');
                  setPhysicianFax(newValue.fax || '');
                  setPhysicianOrganization(newValue.organization || '');
                  if (newValue.email) {
                    setCoordinatorEmail(newValue.email);
                  }
                  setIsExistingPhysician(true);
                } else if (typeof newValue === 'string') {
                  setPhysicianName(newValue);
                  setPhysicianOrganization('');
                  setIsExistingPhysician(false);
                } else {
                  setPhysicianName('');
                  setPhysicianOrganization('');
                  setIsExistingPhysician(false);
                }
              }}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') {
                  setPhysicianName(newInputValue);
                  setPhysicianOrganization('');
                  setIsExistingPhysician(false);
                } else if (reason === 'clear') {
                  setPhysicianName('');
                  setPhysicianPhone('');
                  setPhysicianFax('');
                  setPhysicianOrganization('');
                  setCoordinatorEmail('');
                  setIsExistingPhysician(false);
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id || option.name}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{option.name}</Typography>
                    {(option.specialty || option.organization) && (
                      <Typography variant="caption" color="text.secondary">
                        {[option.specialty, option.organization].filter(Boolean).join(' • ')}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Physician Name"
                  placeholder={!practice ? "Select practice first" : "Search or type physician..."}
                />
              )}
            />
          </Grid>

          {practice && physicianName && (
            <>
              {isExistingPhysician && (
                <Grid item xs={12}>
                  <Alert 
                    severity="success" 
                    icon={false}
                    sx={{ 
                      py: 1.5, 
                      px: 2.5, 
                      borderRadius: 3, 
                      backgroundColor: 'rgba(46, 125, 50, 0.06)',
                      border: '1px solid rgba(46, 125, 50, 0.15)',
                      color: 'success.dark',
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}
                  >
                    🤝 <strong>Dr. {toTitleCase(physicianName)}{physicianOrganization ? ` and ${toTitleCase(physicianOrganization)}` : ''} staff, we always appreciate your referrals!</strong> Thank you for your trust and continued partnership. We have your office phone and fax listed below — if this is inaccurate let us know.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  disabled={isExistingPhysician}
                  label="Physician Phone Number"
                  value={formatPhone(physicianPhone)}
                  onChange={handlePhoneChange(setPhysicianPhone)}
                  placeholder="555-123-4567"
                  inputProps={{ maxLength: 12 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  disabled={isExistingPhysician}
                  label="Physician Fax Number (optional)"
                  value={formatPhone(physicianFax)}
                  onChange={handlePhoneChange(setPhysicianFax)}
                  placeholder="555-123-4567"
                  inputProps={{ maxLength: 12 }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Referral Coordinator Name & Direct Phone"
              value={coordinatorName}
              onChange={(e) => setCoordinatorName(e.target.value)}
              placeholder="Jane Doe - 555-123-4567"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Coordinator Email"
              value={coordinatorEmail}
              onChange={(e) => setCoordinatorEmail(e.target.value)}
              placeholder="coordinator@example.com"
              helperText="Form confirmation will be sent here"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Patient Information */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Patient Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Patient Full Name"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              inputRef={dobInputRef}
              value={patientDOB}
              onChange={(e) => setPatientDOB(e.target.value)}
              onBlur={handleDobBlur}
            />
            <DobConfirmDialog open={showDobConfirmDialog} dob={patientDOB} onFix={fixDob} onConfirm={confirmDob} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Patient Phone Number"
              value={formatPhone(patientPhone)}
              onChange={handlePhoneChange(setPatientPhone)}
              placeholder="555-123-4567"
              inputProps={{ maxLength: 12 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Referral & Insurance Details */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Referral & Insurance Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Reason for Referral"
              value={referralReason}
              onChange={(e) => setReferralReason(e.target.value)}
              placeholder="Describe the clinical reason for this referral..."
            />
          </Grid>
          {(insuranceType === 'HMO' || insuranceType === 'MEDICAID') && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                <Typography variant="body2" fontWeight="bold">
                  HMO/MEDICAID referrals will require an approved auth number from the patient's PCP. If you can provide that, it will expedite our processing.
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              label="Insurance Type"
              value={insuranceType}
              onChange={(e) => {
                const val = e.target.value;
                setInsuranceType(val);
                if (val !== 'Workers Compensation') {
                  setWorkerCompInfo('');
                }
                if (val !== 'HMO' && val !== 'MEDICAID') {
                  setHmoAuthNumber('');
                }
              }}
            >
              {INSURANCE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {(insuranceType === 'HMO' || insuranceType === 'MEDICAID') && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HMO Approval / Auth #"
                value={hmoAuthNumber}
                onChange={(e) => setHmoAuthNumber(e.target.value)}
                helperText="If available, helps expedite processing"
              />
            </Grid>
          )}

          {insuranceType === 'Workers Compensation' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Worker's Comp Info (enter N/A if not applicable)"
                value={workerCompInfo}
                onChange={(e) => setWorkerCompInfo(e.target.value)}
                placeholder="Carrier Name, Case#, Adjuster Name & Ph#, Date of Injury"
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Insurance Carrier"
              value={insuranceCarrier}
              onChange={(e) => setInsuranceCarrier(e.target.value)}
              placeholder="e.g. BCBS, Aetna"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Member ID"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="Member policy ID number"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="HMO / Medicaid PCP"
              value={patientPCP}
              onChange={(e) => setPatientPCP(e.target.value)}
              placeholder="Primary Care Physician (if applicable)"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* File Uploads */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Upload Medical Records & Documents
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            IMPORTANT: Missing progress notes and demographics will delay patient scheduling.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
            Useful documents for us to have:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['PCP Referral', 'H&P', 'Progress Note', 'Patient Demographics', 'Radiology/Lab Reports', 'Patient ID', 'Insurance Card'].map((doc) => (
              <Chip key={doc} label={doc} size="small" variant="outlined" sx={{ borderRadius: 2, fontSize: '0.78rem' }} />
            ))}
          </Box>
        </Box>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
          sx={{
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragging ? 'rgba(102, 126, 234, 0.05)' : 'rgba(0, 0, 0, 0.01)',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'rgba(102, 126, 234, 0.02)',
            },
            mb: 2.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5
          }}
        >
          <input
            id="file-input"
            type="file"
            multiple
            hidden
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          <UploadFileIcon sx={{ fontSize: 40, color: isDragging ? 'primary.main' : 'text.secondary' }} />
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            Drag and drop clinical files here, or <span style={{ color: '#667eea', fontWeight: 'bold' }}>browse files</span>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports PDF, Word (DOC/DOCX), JPG, JPEG, PNG
          </Typography>
        </Box>

        {uploadedFiles.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {uploadedFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  backgroundColor: 'rgba(0,0,0,0.005)',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.015)',
                    borderColor: 'rgba(0,0,0,0.12)'
                  }
                }}
              >
                {/* Thumbnail Preview Column */}
                {renderFilePreview(file)}

                {/* Filename & Info Column */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'semibold',
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.3
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>

                {/* Action Column */}
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'error.main' },
                    mr: 0.5
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {uploadedFiles.length === 0 && (
          <Alert severity="info" sx={{ mt: 1, borderRadius: 3 }}>
            No files chosen yet. Please drag & drop or click to upload required clinical documents.
          </Alert>
        )}
      </Paper>

      {/* Additional Notes */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'medium' }}>
          Additional Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notes or Comments"
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          placeholder="Enter any additional scheduling preferences or clinician comments..."
        />
      </Paper>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          sx={{ minWidth: 250, py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Referral'}
        </Button>

        {!isValid && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Please select practice, enter required patient details, and upload at least one record.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
